/**
 * Frontend Team Filtering Service
 * Handles Global/Team view filtering, team permissions, and data organization
 * All filtering happens client-side for better performance and UX
 */

import {
  CrossoverTeam,
  TeamMemberActivityWithContext,
  MultiTeamDataResult,
  TeamFilterConfig,
  FilteredTeamResult
} from '../types/teamTypes';
import { PersonalizedLeaderboardEntry, LeaderboardEntry, NO_CURSOR_DATA } from '../types';
import { calculateEfficiencyScore } from './personalizedDataService';
import { calculateCursorTime, UserTimeData } from './timeCalculationService';

/**
 * Check if a user is a manager of a specific team
 * @param teamId - Team ID to check
 * @param userManagerId - User's manager ID
 * @param teams - Array of all teams
 * @returns boolean - True if user manages this team
 */
export const isUserManagerOfTeam = (
  teamId: number,
  userManagerId: number,
  teams: CrossoverTeam[]
): boolean => {
  const team = teams.find(t => t.id === teamId);
  if (!team) return false;

  // Check if user is team owner
  if (team.teamOwner.id === userManagerId) {
    return true;
  }

  // Check if user is in reporting managers
  return team.reportingManagers.some(manager => manager.id === userManagerId);
};

/**
 * Filter teams based on user's management permissions
 * @param teams - All available teams
 * @param userManagerId - User's manager ID
 * @returns FilteredTeamResult - Teams categorized by user's role
 */
export const filterTeamsByUserPermissions = (
  teams: CrossoverTeam[],
  userManagerId: number
): FilteredTeamResult => {
  
  const teamOwnerOf: CrossoverTeam[] = [];
  const reportingManagerOf: CrossoverTeam[] = [];
  const accessibleTeams: CrossoverTeam[] = [];

  teams.forEach(team => {
    let hasAccess = false;

    // Check if user is team owner
    if (team.teamOwner.id === userManagerId) {
      teamOwnerOf.push(team);
      hasAccess = true;
    }

    // Check if user is reporting manager
    if (team.reportingManagers.some(manager => manager.id === userManagerId)) {
      reportingManagerOf.push(team);
      hasAccess = true;
    }

    if (hasAccess) {
      accessibleTeams.push(team);
    }
  });

  return {
    accessibleTeams,
    teamOwnerOf,
    reportingManagerOf,
    filterConfig: {
      loggedInManagerId: userManagerId,
      includeOwnedTeams: true,
      includeReportingTeams: true
    }
  };
};

/**
 * Filter team activities for Central PS Global/Team View 
 * Only needed when using service account data (fetchComprehensiveData)
 * NOT needed for My Team mode (fetchManagerTeamData) as API already filters
 * @param activities - All team member activities
 * @param userManagerId - User's manager ID  
 * @param teams - All teams data
 * @returns TeamMemberActivityWithContext[] - Filtered activities
 */
export const filterActivitiesForCentralPS = (
  activities: TeamMemberActivityWithContext[],
  userManagerId: number,
  teams: CrossoverTeam[],
  viewMode: 'global' | 'team'
): TeamMemberActivityWithContext[] => {
  
  console.log('🔍 [FILTER] Filtering activities for Central PS view:', {
    viewMode,
    totalActivities: activities.length,
    userManagerId,
    totalTeams: teams.length
  });

  if (viewMode === 'global') {
    // For global view, return all activities (no filtering)
    console.log('🌍 [FILTER] Global view - returning all activities');
    return activities;
  }

  // For team view, filter to only teams the user manages
  const filteredActivities = activities.filter(activity => 
    isUserManagerOfTeam(activity.teamId, userManagerId, teams)
  );

  console.log('✅ [FILTER] Central PS Team View filtering complete:', {
    originalCount: activities.length,
    filteredCount: filteredActivities.length,
    removedCount: activities.length - filteredActivities.length
  });

  return filteredActivities;
};

/**
 * Merge Cursor statistics with team activity data for comprehensive view
 * @param cursorUsers - Users from Cursor statistics
 * @param teamActivities - Team member activities from Crossover
 * @returns PersonalizedLeaderboardEntry[] - Merged data with graceful handling
 */
export const mergeComprehensiveUserData = (
  cursorUsers: LeaderboardEntry[],
  teamActivities: TeamMemberActivityWithContext[]
): PersonalizedLeaderboardEntry[] => {
  
  console.log('🔗 [FILTER] CORRECT ALGORITHM: Team members as master, match cursor data by companyEmail only');
  console.log('📊 [FILTER] Input data:', {
    cursorUsersCount: cursorUsers.length,
    teamActivitiesCount: teamActivities.length
  });

  const mergedUsers: PersonalizedLeaderboardEntry[] = [];

  // Create a map of cursor users by normalized email for efficient lookup
  const cursorMap = new Map<string, LeaderboardEntry>();
  cursorUsers.forEach(cursorUser => {
    const normalizedEmail = cursorUser.email.toLowerCase().trim();
    cursorMap.set(normalizedEmail, cursorUser);
  });

  console.log(`🗂️ [FILTER] Created cursor lookup map with ${cursorMap.size} entries`);

  // MASTER LIST: Process team members and find their cursor data (by companyEmail ONLY)
  teamActivities.forEach((teamActivity, index) => {
    const teamUserId = teamActivity.assignment?.candidate?.id?.toString();
    const companyEmail = (teamActivity as any).assignmentHistory?.companyEmail || '';
    const teamMemberName = teamActivity.assignment?.candidate?.printableName || companyEmail;
    const cursorTimeMinutes = extractCursorTimeFromActivity(teamActivity);

    if (!teamUserId || !companyEmail) {
      console.warn(`⚠️ [FILTER] Skipping team member - missing data:`, {
        teamMemberName,
        hasUserId: !!teamUserId,
        hasCompanyEmail: !!companyEmail
      });
      return;
    }

    // Try to find cursor data for this team member (by companyEmail ONLY)
    const normalizedCompanyEmail = companyEmail.toLowerCase().trim();
    const cursorData = cursorMap.get(normalizedCompanyEmail);

    if (cursorData) {
      // Team member HAS cursor data - use their aggregated weekly stats
      const efficiencyScore = calculateEfficiencyScore(cursorData.chatAcceptedLinesAdded, cursorTimeMinutes);
      
      const teamMemberWithCursorData: PersonalizedLeaderboardEntry = {
        ...cursorData, // Preserve all aggregated cursor stats
        userId: teamUserId, // Use team userId
        email: companyEmail, // Use companyEmail for consistency
        cursorTimeMinutes,
        teamMemberName,
        efficiencyScore
      };

      mergedUsers.push(teamMemberWithCursorData);
    } else {
      // Team member has NO cursor data - show '-' for cursor metrics  
      const teamMemberNoCursorData: PersonalizedLeaderboardEntry = {
        userId: teamUserId,
        email: companyEmail,
        chatTotalApplies: NO_CURSOR_DATA,
        chatTotalAccepts: NO_CURSOR_DATA,
        chatAcceptedLinesAdded: NO_CURSOR_DATA,
        totalLinesAdded: NO_CURSOR_DATA,
        chatTabsShown: NO_CURSOR_DATA,
        tabsAccepted: NO_CURSOR_DATA,
        editRequests: NO_CURSOR_DATA,
        askRequests: NO_CURSOR_DATA,
        agentRequests: NO_CURSOR_DATA,
        cmdkRequests: NO_CURSOR_DATA,
        cursorTimeMinutes,
        teamMemberName,
        efficiencyScore: 0 // No efficiency without cursor data
      };

      mergedUsers.push(teamMemberNoCursorData);
    }
  });

  console.log('✅ [FILTER] CORRECT MERGE complete:', {
    totalTeamMembers: mergedUsers.length,
    withCursorData: mergedUsers.filter(u => u.chatTotalApplies !== NO_CURSOR_DATA).length,
    withoutCursorData: mergedUsers.filter(u => u.chatTotalApplies === NO_CURSOR_DATA).length
  });

  return mergedUsers;
};

/**
 * Extract cursor time from team activity data
 * @param activity - Team member activity
 * @returns number - Cursor time in minutes
 */
const extractCursorTimeFromActivity = (activity: TeamMemberActivityWithContext): number => {
  // Convert to UserTimeData format for the new calculation service
  const userData: UserTimeData = {
    grouping: {
      periodLong: activity.grouping?.periodLong || 0,
      totalTrackedTime: activity.grouping?.totalTrackedTime || 0,
      alignmentScore: activity.grouping?.alignmentScore || 0,
      advancedGroups: activity.grouping?.advancedGroups || []
    }
  };
  
  // Use the new standardized calculation service
  return calculateCursorTime(userData.grouping.advancedGroups);
};

/**
 * Main filtering coordinator - handles different data sources appropriately
 * @param allData - Complete multi-team data
 * @param cursorUsers - Cursor statistics data
 * @param userManagerId - Logged-in user's manager ID
 * @param dataSource - Where the data came from ('central-ps' or 'my-team')
 * @param viewMode - 'global' or 'team' (only relevant for Central PS)
 * @returns PersonalizedLeaderboardEntry[] - Filtered and merged data
 */
export const getFilteredLeaderboardData = (
  allData: MultiTeamDataResult,
  cursorUsers: LeaderboardEntry[],
  userManagerId: number,
  dataSource: 'central-ps' | 'my-team',
  viewMode: 'global' | 'team' = 'team'
): PersonalizedLeaderboardEntry[] => {
  
  console.log('🎯 [FILTER] Getting filtered leaderboard data:', {
    dataSource,
    viewMode,
    userManagerId,
    totalTeams: allData.teams.length,
    totalActivities: allData.activities.length,
    totalCursorUsers: cursorUsers.length
  });

  let filteredActivities: TeamMemberActivityWithContext[];

  if (dataSource === 'my-team') {
    // For "My Team" mode: API already filtered data, no additional filtering needed
    console.log('👥 [FILTER] My Team mode - API already filtered data, using as-is');
    filteredActivities = allData.activities;
  } else {
    // For "Central PS" mode: Service account data needs filtering based on user permissions
    console.log('🏢 [FILTER] Central PS mode - applying permission-based filtering');
    filteredActivities = filterActivitiesForCentralPS(
      allData.activities, 
      userManagerId, 
      allData.teams, 
      viewMode
    );
  }

  // Merge Cursor and team data
  const mergedData = mergeComprehensiveUserData(cursorUsers, filteredActivities);

  console.log('✅ [FILTER] Filtering complete:', {
    dataSource,
    viewMode,
    finalUserCount: mergedData.length
  });

  return mergedData;
}; 